import { useQuery } from 'react-query';

import type {
  AgentGateway,
  PatientChatPatientSummary,
  PatientChatQuestionRange,
} from '@mummssoftware/utils';

import { maiaQueryKeys } from '../queryClient';
import { errorMessage, unwrap } from '../utils/gatewayError';
import defaults from '../translations/en.json';

type UsePatientChatPatientsResult = {
  /** null until the first load resolves, so the grid can show its loading overlay. */
  rows: PatientChatPatientSummary[] | null;
  error: string | null;
};

/**
 * MAIA Chats (no patient): every patient of the site with MAIA chats, one row
 * each. The site is the one picked in the dashboard URL; with none, nothing
 * loads and the list shows the load error. The gateway sends the whole list,
 * last question first; the grid sorts and
 * filters it. Loads each time the list is shown; the tile and expanded copies
 * share that request (queryClient.ts). With a range, the gateway counts only
 * the conversations with a question in it; the rows of the last range stay
 * until the new ones arrive.
 */
export const usePatientChatPatients = (
  agentGateway: AgentGateway,
  agency: string,
  siteId: string | undefined,
  isShown: boolean,
  range?: PatientChatQuestionRange | null,
): UsePatientChatPatientsResult => {
  const query = useQuery(
    maiaQueryKeys.patients(agency, siteId, range),
    async () =>
      unwrap<PatientChatPatientSummary[]>(
        await agentGateway.listPatientChatPatients(siteId as string, range),
      ) || [],
    { enabled: isShown && !!siteId, staleTime: 0, keepPreviousData: true },
  );

  if (isShown && !siteId) {
    return { rows: [], error: defaults.loadPatientsError };
  }
  return {
    rows: query.data ?? (query.error ? [] : null),
    error: query.error
      ? errorMessage(query.error, defaults.loadPatientsError)
      : null,
  };
};
